import {FabricText} from 'fabric';

export const PLAYER_INFO_OFFSET_TOP = 13;
export const CIRCLE_INFO_OFFSET_TOP = 10;

export const sizeConverter = {
    'S': 1,
    'M': 2,
    'L': 3,
    'XL': 4
};

export const sizeOptions = {
    1: {
        label: 'S',
        player: {
            size: 70,
            nameTop: 50,
        },
        circle: {
            size: 20,
            nameTop: 43,
        }
    },
    2: {
        label: 'M',
        player: {
            size: 80,
            nameTop: 56,
        },
        circle: {
            size: 30,
            nameTop: 53,
        }
    },
    3: {
        label: 'L',
        player: {
            size: 90,
            nameTop: 63,
        },
        circle: {
            size: 40,
            nameTop: 63,
        }
    },
    4: {
        label: 'XL',
        player: {
            size: 110,
            nameTop: 77,
        },
        circle: {
            size: 50,
            nameTop: 73,
        }
    },
};

export const DEFAULT_SIZE = 2;

export const mapPlayerSize = (size) => {
    return sizeConverter[size];
};

export const getPlayerSizeOptions = (sizeValue = DEFAULT_SIZE) => {
    return sizeOptions[sizeValue].player;
};

export const getCircleSizeOptions = (sizeValue = DEFAULT_SIZE) => {
    return sizeOptions[sizeValue].circle;
};

export const getNextPlayerNumber = (canvas, teamId) => {
    if (!canvas) {
        return 1;
    }

    const max = Math.max(...canvas.getObjects()
        .filter(e => e.custom?.type === 'player' && e.custom?.team === teamId)
        .flatMap(e => e.getObjects().filter(e => e.custom?.type === 'player-number')).map(e => e.text));
    console.log('nextPlayerNumber: ', max);
    return max > 0 ? max + 1 : 1;
};

export const getPlayerById = (canvas, playerId) => {
    return canvas?.getObjects().find(e => e.custom?.type === 'player' && e.custom?.playerId === playerId);
};

export const hasTeamPlayersOnCanvas = (canvas, teamId) => {
    return canvas?.getObjects().some(obj => obj.custom?.team === teamId);
};

export const createPlayerNumber = (number, textColor, size) => {
    return new FabricText(number, {
        custom: {
            type: 'player-number'
        },
        fontSize: size,
        fontWeight: 'bold',
        originX: 'center',
        originY: 'center',
        left: 0,
        top: 0,
        fill: textColor,
        fontFamily: 'Arial'
    });
};

const playerTextProps = (type) => {
    return {
        custom: {
            type
        },
        fontSize: 28,
        fontWeight: 'bold',
        originX: 'center',
        originY: 'center',
        fill: '#000000',
        fontFamily: 'Arial',
        textAlign: 'center',
    };
};

export const createPlayerName = (name, top) => {
    return new FabricText(name, {
        ...playerTextProps('player-name'),
        left: 0,
        top,
        fontSize: 28
    });
};

export const createPlayerInfo = (info, top) => {
    return new FabricText(info, {
        ...playerTextProps('player-info'),
        left: 0,
        top,
        fontSize: 20
    });
};

const findElementByType = (target, type, customType) => {
    return target.getObjects().find(o => {
        return o.custom && o.custom.type === customType;
    });
};

const getElementText = (target, customType) => {
    if (!target || !target.custom) return '';

    const elem = findElementByType(target, 'text', customType);

    if (!elem) {
        return '';
    }

    const txt = elem.get('text');

    if (txt[0] === ' ') {
        txt.substring(1, txt.length - 2);
    }

    return txt;
};

const isCircleType = (target) =>
    target.custom?.subType === 'playerCircle' || target.custom?.subType === 'playerCircleImage';

/**
 * Returns the nameTop offset for a given player target
 */
const getNameTop = (target) => {
    if (isCircleType(target)) {
        return getCircleSizeOptions(target.custom?.size).nameTop;
    }

    return getPlayerSizeOptions(target.custom?.size).nameTop;
};

/**
 * @param target {fabric.Group}
 * @return {string}
 */
export const getPlayerName = (target) => {
    return getElementText(target, 'player-name');
};

/**
 * Adds an object to a group with desired group-relative coordinates.
 * fabric v7 Group.add() calls enterGroup(obj, true) which transforms
 * canvas-absolute coords to group-relative. So we convert our desired
 * group-relative coords to canvas-absolute first.
 */
const addToGroup = (group, object) => {
    const center = group.getCenterPoint();
    object.set({
        left: center.x + object.left,
        top: center.y + object.top,
    });
    group.add(object);
};

/**
 * @param target {fabric.Group}
 * @param name {string}
 */
export const setPlayerName = (target, name) => {
    if (!target || !target.custom) return;
    const elem = findElementByType(target, 'text', 'player-name');
    const elemInfo = findElementByType(target, 'text', 'player-info');

    const nameTop = getNameTop(target);

    if (elem) {
        if (name.length === 0) {
            if (elemInfo) {
                elem.set('text', '');
                if (target.canvas) target.canvas.renderAll();
                return;
            }

            target.remove(elem);
        } else {
            elem.set('text', name);
        }
    } else {
        if (name.length === 0) {
            return;
        }

        addToGroup(target, createPlayerName(name, nameTop));
    }

    if (target.canvas) {
        target.canvas.renderAll();
    }
};

/**
 * @param target {fabric.Group}
 * @return {string}
 */
export const getPlayerInfo = (target) => {
    return getElementText(target, 'player-info');
};

/**
 * @param target {fabric.Group}
 * @param info {string}
 */
export const setPlayerInfo = (target, info) => {
    if (!target || !target.custom) return;

    const elem = findElementByType(target, 'text', 'player-info');
    let elemName = findElementByType(target, 'text', 'player-name');

    const nameTop = getNameTop(target);
    const infoOffset = isCircleType(target) ? CIRCLE_INFO_OFFSET_TOP : PLAYER_INFO_OFFSET_TOP;
    const infoTop = nameTop + infoOffset;

    if (elem) {
        if (info.length === 0) {
            target.remove(elem);

            if (elemName && !elemName.get('text')) {
                target.remove(elemName);
            }
        } else {
            elem.set('text', info);
        }
    } else {
        if (info.length === 0) {
            return;
        }

        // Ensure a name element exists (even empty) so info sits below it
        if (!elemName) {
            addToGroup(target, createPlayerName('', nameTop));
        }

        addToGroup(target, createPlayerInfo(info, infoTop));
    }

    if (target.canvas) {
        target.canvas.renderAll();
    }
};

/**
 * @param target {fabric.Group}
 * @return {string}
 */
export const getPlayerNumber = (target) => {
    if (!target || !target.custom) return '';

    const elem = findElementByType(target, 'text', 'player-number');

    return elem ? elem.get('text') : '';
};

/**
 * @param target {fabric.Group}
 * @param number {string}
 */
export const setPlayerNumber = (target, number) => {
    if (!target || !target.custom) return;

    const elem = findElementByType(target, 'text', 'player-number');

    if (elem) {
        elem.set('text', number);
        target.canvas.renderAll();
    }
};
